import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { Btn, Spinner } from '../../../AbstractElements';
import { Card, CardBody, Col, FormGroup, Label, Row } from 'reactstrap';
import axios from 'axios';
import config from '../../../config';
import { toast } from 'react-toastify';

import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// source: 'live' (default) | 'uat' - selects which set of collections the grid reads.
const DataGridInvoiceWiseView = ({ source = 'live' }) => {
    if (localStorage.getItem("accessDataGrid") !== "1") {
        alert('You are not authorized to view this page');
        window.history.back();
    }

    const isUat = source === 'uat';

    const gridUrl = isUat
        ? `${config.hostname}/datagrid/getdatagridinvoicewiseuat`
        : `${config.hostname}/datagrid/getdatagridinvoicewise`;

    const gridApiRef = useRef(null);

    const PAGE_SIZE = 100;

    const concessionaire_id = localStorage.getItem("concessionaireId");
    const [propertyAccessNames] = useState('');
    const login_name = localStorage.getItem("loginName");
    const login_id = localStorage.getItem("loginId");

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [loading, setLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [rows, setRows] = useState([]);        // current page rows
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);         // 1-based page index

    const today = new Date().toISOString().split('T')[0];
    const token = localStorage.getItem("sessiontoken");
    const configheader = useMemo(() => ({ headers: { Authorization: `${token}` } }), [token]);

    const maxPaymentMethods = useMemo(
        () => rows.reduce(
            (max, r) => Math.max(max, Array.isArray(r.payment_methods) ? r.payment_methods.length : 0),
            0
        ),
        [rows]
    );

    const columns = useMemo(() => {
        const base = [
            { field: 'tenant_store_name', headerName: 'Tenant Name', width: 150 },
            { field: 'tenant_group_name', headerName: 'Group Name', width: 200 },
            { field: 'tenant_code', headerName: 'Tenant Code', width: 150 },
            { field: 'tenant_unit_code', headerName: 'Unit Code', width: 150 },
            { field: 'tenant_status', headerName: 'Status', width: 150 },
            { field: 'property_name', headerName: 'Property', width: 150 },
            { field: 'subcategory_name', headerName: 'Subcategory', width: 200 },
            { field: 'floor', headerName: 'Floor', width: 100 },
            { field: 'transaction_date', headerName: 'Date', width: 150 },
            {
                field: 'invoice_no',
                headerName: 'Invoice No',
                width: 150,
                valueFormatter: p =>
                    p.value === '' || p.value === null || p.value === undefined ? '-' : p.value
            },
            {
                field: 'transaction_time',
                headerName: 'Transaction Time',
                width: 150,
                valueFormatter: p =>
                    p.value === '' || p.value === null || p.value === undefined ? '-' : p.value
            },
            {
                field: 'transaction_type',
                headerName: 'Transaction Type',
                width: 150,
                valueFormatter: p =>
                    p.value === '' || p.value === null || p.value === undefined ? '-' : p.value
            },
            {
                field: 'currency',
                headerName: 'Currency',
                width: 100,
                valueFormatter: p =>
                    p.value === '' || p.value === null || p.value === undefined ? '-' : p.value
            },
            {
                field: 'sales_channel',
                headerName: 'Sales Channel',
                width: 150,
                valueFormatter: p =>
                    p.value === '' || p.value === null || p.value === undefined ? '-' : p.value
            },
            {
                field: 'shift',
                headerName: 'Shift',
                width: 100,
                valueFormatter: p =>
                    p.value === '' || p.value === null || p.value === undefined ? '-' : p.value
            },
            {
                field: 'quantity',
                headerName: 'Qty',
                width: 90,
                type: 'number',
                valueFormatter: p => p.value === '' || p.value === null || p.value === undefined ? '-' : p.value
            },
            {
                field: 'trans_net_sales',
                headerName: 'Net Sales',
                width: 150,
                type: 'number',
                valueFormatter: p => p.value != null ? Number(p.value).toFixed(2) : '0.00'
            },
            {
                field: 'trans_tax_amount',
                headerName: 'Tax Amount',
                width: 150,
                type: 'number',
                valueFormatter: p => p.value != null ? Number(p.value).toFixed(2) : '0.00'
            },
            {
                field: 'trans_gross_amount',
                headerName: 'Gross Amount',
                width: 150,
                type: 'number',
                valueFormatter: p => p.value != null ? Number(p.value).toFixed(2) : '0.00'
            },
            {
                field: 'trans_discount_amount',
                headerName: 'Discount Amount',
                width: 160,
                type: 'number',
                valueFormatter: p => p.value != null ? Number(p.value).toFixed(2) : '0.00'
            },
            {
                field: 'trans_return_amount',
                headerName: 'Return Amount',
                width: 150,
                type: 'number',
                valueFormatter: p => p.value != null ? Number(p.value).toFixed(2) : '0.00'
            }
        ];

        // One { Payment Method N, Payment Amount N } pair per slot in the widest
        const paymentColumns = [];
        for (let i = 0; i < maxPaymentMethods; i++) {
            paymentColumns.push(
                {
                    colId: `payment_method_${i + 1}`,
                    headerName: `Payment Method ${i + 1}`,
                    width: 160,
                    valueGetter: p => p.data?.payment_methods?.[i]?.payment_method_name ?? '-',
                    filter: 'agTextColumnFilter'
                },
                {
                    colId: `payment_amount_${i + 1}`,
                    headerName: `Payment Amount ${i + 1}`,
                    width: 160,
                    type: 'number',
                    valueGetter: p => {
                        const v = p.data?.payment_methods?.[i]?.payment_amount;
                        return v === '' || v === null || v === undefined ? null : Number(v);
                    },
                    valueFormatter: p => p.value != null ? Number(p.value).toFixed(2) : '-'
                }
            );
        }

        return [...base, ...paymentColumns];
    }, [maxPaymentMethods]);

    const insertToLog = (task) => {
        const payload = {
            created_by: login_id,
            concessionaire_id,
            module: isUat ? "UAT Data Grid" : "Data Grid",
            task: login_name + task
        };
        axios.post(`${config.hostname}/log`, payload, configheader).catch(() => { });
    };

    // state/refs
    const filterModelRef = useRef({});
    const sortModelRef = useRef([]);
    const filterDebounce = useRef(null);

    // grid events
    const onGridReady = (p) => { gridApiRef.current = p.api; };

    const onFilterChanged = () => {
        if (!gridApiRef.current) return;
        filterModelRef.current = gridApiRef.current.getFilterModel() || {};
        clearTimeout(filterDebounce.current);
        filterDebounce.current = setTimeout(() => fetchPage(1), 350); // debounce typing
    };

    const onSortChanged = (params) => {
        const api = params?.api || gridApiRef.current;
        if (!api) return;

        const sortModel = (api.getColumnState?.() || [])
            .filter(col => col.sort)
            .map(col => ({
                colId: col.colId,
                sort: col.sort
            }));

        sortModelRef.current = sortModel;
        fetchPage(1);
    };

    const fetchPage = async (pageIndex) => {

        if (startDate === '' || endDate === '') {
            toast.warning('Select start date and end date')
            return
        }
        try {
            const skip = (pageIndex - 1) * PAGE_SIZE;
            const firstSort = (sortModelRef.current && sortModelRef.current[0]) || {};
            const sortField = firstSort.colId || undefined;
            const sortDir = firstSort.sort || undefined;

            const payload = {
                concessionaire_id,
                property_access: propertyAccessNames,
                start_date: startDate,
                end_date: endDate,
                skip,
                limit: PAGE_SIZE,
                filterModel: filterModelRef.current,
                sortField,
                sortDir,
                source
            };

            const res = await axios.post(
                gridUrl,
                payload,
                configheader
            );

            const gridRows = Array.isArray(res.data?.data) ? res.data.data : [];
            setRows(gridRows);
            setTotalCount(Number(res.data?.totalCount || gridRows.length || 0));
            setPage(pageIndex);
        } catch (error) {
            console.error("Error fetching page data:", error.message);

            setRows([]);
            setTotalCount(0);

            toast.error("No data");
        }
    };


    const defaultColDef = {
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        resizable: true,
        filterParams: { debounceMs: 300, suppressAndOrCondition: true, caseSensitive: false }
    };



    const onClickLoadData = () => {
        fetchPage(1);
    };

    const canPrev = page > 1;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const canNext = page < totalPages;

    const goPrev = () => { if (canPrev) fetchPage(page - 1); };
    const goNext = () => { if (canNext) fetchPage(page + 1); };



    const reset = () => {
        setStartDate('');
        setEndDate('');
        setRows([]);
        setTotalCount(0);
        setPage(1);
        setValidationMessage('');
    };

    const clearGrid = () => {
        setRows([]);
        setTotalCount(0);
        setPage(1);
    };

    const onStartDateChange = (e) => {
        setStartDate(e.target.value);
        clearGrid();
        setValidationMessage('');
    };

    const onEndDateChange = (e) => {
        setEndDate(e.target.value);
        clearGrid();
        setValidationMessage('');
    };

    // Streams the CSV from the server (single environment, matching the grid) instead
    // of paging through rows and building the file in the browser.
    const exportFullCsvServer = async () => {
        if (!startDate || !endDate) { toast.warn('Select both dates first'); return; }
        if (endDate < startDate) { toast.warn('Fix the date range'); return; }

        try {
            setLoading(true);

            const firstSort = (sortModelRef.current && sortModelRef.current[0]) || {};
            const sortField = firstSort.colId || undefined;
            const sortDir = firstSort.sort || undefined;

            const payload = {
                concessionaire_id,
                property_access: propertyAccessNames || '',
                start_date: startDate,
                end_date: endDate,
                filterModel: filterModelRef.current || {},
                sortField,
                sortDir,
                source
            };

            const res = await axios.post(
                `${config.hostname}/datagridinvoiceexport/export-csv`,
                payload,
                {
                    headers: { Authorization: localStorage.getItem('sessiontoken') },
                    responseType: 'blob',
                }
            );

            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${isUat ? 'uat_invoice_grid' : 'invoice_grid'}_${startDate}_${endDate}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            insertToLog(' exported Invoice Grid data to CSV');
        } catch (e) {
            console.error(e);
            toast.error('Export failed');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Fragment>
            <div className='theme-form'>
                <Card>
                    <CardBody>
                        <Row>
                            <Col>
                                <FormGroup className="mb-3">
                                    <Label htmlFor="startdate">Start Date</Label>
                                    <input
                                        className="form-control digits"
                                        id="startdate"
                                        type="date"
                                        value={startDate}
                                        max={today}
                                        onChange={onStartDateChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup className="mb-3">
                                    <Label htmlFor="enddate">End Date</Label>
                                    <input
                                        className="form-control digits"
                                        id="enddate"
                                        type="date"
                                        value={endDate}
                                        max={today}
                                        onChange={onEndDateChange}
                                    />
                                </FormGroup>
                                {validationMessage && <span className="text-danger">{validationMessage}</span>}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Btn attrBtn={{ color: 'primary', className: 'me-3', onClick: onClickLoadData }}>
                                    Load Data
                                </Btn>
                                <Btn attrBtn={{ color: 'primary', className: 'me-3', onClick: exportFullCsvServer }}>
                                    Export to CSV
                                </Btn>
                                <Btn attrBtn={{ color: 'secondary', className: 'me-3', onClick: reset }}>
                                    Reset
                                </Btn>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </div>

            {loading ? (
                <div className="loader-box">
                    <Spinner attrSpinner={{ className: 'loader-3' }} />
                </div>
            ) : (
                <>
                    <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
                        <AgGridReact
                            rowData={Array.isArray(rows) ? rows : []}
                            columnDefs={columns}
                            defaultColDef={defaultColDef}
                            onGridReady={onGridReady}
                            onFilterChanged={onFilterChanged}
                            onSortChanged={onSortChanged}
                            pagination={false}
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <Btn attrBtn={{ color: 'light', className: 'me-2', onClick: goPrev, disabled: !canPrev }}>
                            ◀ Prev
                        </Btn>
                        <span>Page {page} of {totalPages} &nbsp;|&nbsp; {totalCount} rows</span>
                        <Btn attrBtn={{ color: 'light', className: 'ms-2', onClick: goNext, disabled: !canNext }}>
                            Next ▶
                        </Btn>
                    </div>
                </>
            )}
        </Fragment>
    );
};

export default DataGridInvoiceWiseView;
